import React from 'react';
import { CheckIcon } from '@assets/icons';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useToast } from '@withjoy/joykit';
import { AccommodationPlaceType, useAddAccommodationPlaceV2Mutation, useUpdateAccommodationPlaceV2Mutation } from '@graphql/generated';
import { HotelFormFields } from '../pages/Hotel/HotelForm.types';
import { useUpdateAccommodationsSortOrder } from '@apps/admin/common/hooks/useUpdateAccommodationsSortOrder';
import { convertFormFieldsToAddAccommodationPayload, convertFormFieldsToUpdateAccommodationPayload } from '../utils/Accommodations.utils';

interface ManageAccommodationPlaceArgs
  extends Readonly<{
    id?: string | null;
    isEditMode: boolean;
    onSuccess: () => void;
  }> {}

/**
 * Custom hook to manage adding and updating accommodation places in the Travel Map.
 * Handles GraphQL mutations, error reporting, toast notifications, and preview updates.
 *
 * @param args - Arguments for managing accommodation place actions.
 * @param args.id - The ID of the accommodation place to update (required in edit mode).
 * @param args.isEditMode - Whether the operation is editing an existing accommodation place.
 * @param args.onSuccess - Callback invoked after a successful add or update operation.
 *
 * @returns An object containing:
 *   - saveAccommodationPlace: Function to save (add or update) an accommodation place.
 *   - savingAccommodationPlace: Boolean indicating if a save operation is in progress.
 */
export const useManageAccommodationPlace = ({ id, isEditMode, onSuccess }: ManageAccommodationPlaceArgs) => {
  const { toast } = useToast();
  const { eventInfo } = useEventInfo();
  const { tq } = useTranslation('travelMap');
  const { updateSortOrder } = useUpdateAccommodationsSortOrder(onSuccess);

  // Error handler for add/update mutations
  const handleError = useEventCallback((error: unknown, source: string) => {
    addError(error, { context: 'useManageAccommodationPlace', source });
    toast(tq`Failed to save accommodation. Please try again.`, { icon: <WarningTriangle /> });
  });

  // Success handler for add/update mutations
  const handleSuccess = useEventCallback(() => {
    toast(tq`Accommodation saved successfully.`, { icon: <CheckIcon /> });
  });

  const [addAccommodationPlaceV2, { loading: isLoadingCreationV2 }] = useAddAccommodationPlaceV2Mutation({
    onCompleted: handleSuccess,
    onError: error => handleError(error, 'useAddAccommodationPlaceV2Mutation'),
  });

  const [updateAccommodationPlaceV2, { loading: isLoadingUpdateV2 }] = useUpdateAccommodationPlaceV2Mutation({
    onCompleted: handleSuccess,
    onError: error => handleError(error, 'useUpdateAccommodationPlaceV2Mutation'),
  });

  /**
   * Saves an accommodation place (add or update).
   * Optimized to avoid unnecessary renders and side effects.
   *
   * @param values - Form values for the hotel.
   * @param isEditMode - Whether editing an existing accommodation.
   * @param id - Accommodation ID (required for edit).
   */
  const saveAccommodationPlace = useEventCallback(async (values: HotelFormFields) => {
    if (isEditMode && id) {
      const payload = convertFormFieldsToUpdateAccommodationPayload(id, values);
      await updateAccommodationPlaceV2({
        variables: { payload },
        awaitRefetchQueries: true,
        refetchQueries: ['AccommodationHotels', 'AccommodationPlacesByCouple'],
      });
      onSuccess();
      return;
    }

    if (!eventInfo?.eventId) {
      handleError(new Error('Event ID is missing'), 'saveAccommodationPlace - eventInfo');
      return;
    }

    const payload = convertFormFieldsToAddAccommodationPayload(eventInfo.eventId, values);
    if (!payload.sourceOfData) {
      handleError(new Error('Source of data is missing'), 'saveAccommodationPlace - addAccommodationPlaceV2');
      return;
    }

    const result = await addAccommodationPlaceV2({
      variables: {
        payload: {
          ...payload,
          type: AccommodationPlaceType.hotel,
          sourceOfData: payload.sourceOfData,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: ['AccommodationHotels', 'AccommodationPlacesByCouple'],
    });

    const newAccommodationPlaceId = result?.data?.addAccommodationPlaceV2?.id;
    if (newAccommodationPlaceId) {
      updateSortOrder({ accommodationsPlaceIds: [newAccommodationPlaceId], action: 'add' });
    }
  });

  return {
    saveAccommodationPlace,
    savingAccommodationPlace: isLoadingCreationV2 || isLoadingUpdateV2,
  };
};
